// ═══════════════════════════════════════════════════════════════
// BillingBanners.jsx — Billing status banners
// Priority order: past_due > cancelled > trial (only one shown)
// Grandfathered users see no banner.
// ═══════════════════════════════════════════════════════════════

import React, { useCallback } from 'react';
import { AlertCircle, Clock } from 'lucide-react';
import { useBilling } from './BillingProvider.jsx';
import { getTrialStatus } from './BillingEnforcement.js';
import { useIsTwaSubscriptionGated } from '../../TwaContext.jsx';

/**
 * <BillingBanners onError={(msg) => toast(msg)} />
 *
 * Renders the appropriate billing banner based on subscription state.
 * Must be used inside a <BillingProvider>.
 */
function BillingBanners({ onError }) {
  const { billing, stripeAdapter, user, isGrandfathered } = useBilling();
  const twaSubGated = useIsTwaSubscriptionGated(user);

  const handlePortal = useCallback(async () => {
    try {
      const data = await stripeAdapter.createPortalSession({
        userId: user.id,
        returnUrl: window.location.href,
      });
      if (data.url) {
        window.location.href = data.url;
      } else {
        onError?.(data.error || 'Failed to open billing portal.');
      }
    } catch (e) {
      console.error('Portal error:', e);
      onError?.('Could not access billing portal.');
    }
  }, [stripeAdapter, user?.id, onError]);

  // No banner for grandfathered users or users without billing
  if (isGrandfathered || !billing) return null;

  const status = billing.planStatus;
  const trial = getTrialStatus(billing, null);

  // Past due — highest priority
  if (status === 'past_due') {
    return (
      <div className="max-w-7xl mx-auto px-3 sm:px-6 pt-4">
        <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-red-700 flex-shrink-0" />
          <p className="text-sm text-red-800 flex-1">
            {twaSubGated
              ? 'Your payment is past due. Sign in at theargumentor.com from a web browser to update your payment method.'
              : 'Your payment is past due. Please update your payment method to avoid losing access.'}
          </p>
          {billing.stripeCustomerId && !twaSubGated && (
            <button
              onClick={handlePortal}
              className="px-3 py-1.5 bg-red-100 text-red-700 hover:bg-red-200 rounded-lg text-xs font-medium transition-colors whitespace-nowrap"
            >
              Update Payment
            </button>
          )}
        </div>
      </div>
    );
  }

  // TenantLifecycleGate owns the ended-membership notice.
  if (status === 'cancelled') return null;

  // Trial countdown — only show within 24 hours of charge
  if (trial.inTrial && !trial.expired && trial.daysRemaining <= 1) {
    return (
      <div className="max-w-7xl mx-auto px-3 sm:px-6 pt-4">
        <div className="border rounded-xl px-4 py-3 flex items-center gap-3 bg-amber-50 border-amber-300">
          <Clock className="w-5 h-5 flex-shrink-0 text-amber-700" />
          <p className="text-sm flex-1 text-amber-800">
            Your free trial ends tomorrow. Your card will be charged automatically when the trial ends.
          </p>
        </div>
      </div>
    );
  }

  return null;
}

export default BillingBanners;
